import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const INITIAL_FORM = {
  chain: "bsc",
  tokenAddress: "",
  tokenName: "",
  symbol: "",
  decimals: "18",
  ownerAddress: "",
  totalSupply: "",
  telegram: "",
  contactEmail: "",
  website: "",
  testTxHash: "",
  additionalDetails: "",
  affordMonthly: "yes",
  hasTax: false,
  taxRate: "",
  taxMarketingWallet: "",
  taxMarketingPct: "",
  taxDevWallet: "",
  taxDevPct: "",
  taxLpWallet: "",
  taxLpPct: "",
  billingWallet: "",
  managementWallet: "",
  swapbackEnabled: false,
  swapbackThreshold: "",
  swapbackRouter: process.env.REACT_APP_BESC_ROUTER || "",
  swapbackTokenOut: process.env.REACT_APP_BESC_WNATIVE || "",
};

const ApplyBridgePage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const checkWallet = useMemo(
    () => process.env.REACT_APP_LISTING_CHECK_RECEIVER || process.env.REACT_APP_MINT_CONTROLLER_ADDRESS,
    []
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((previous) => ({ ...previous, [name]: nextValue }));
  };

  const showStatus = (message, tone = "info") => {
    setStatusMessage(message);
    setStatusTone(tone);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    showStatus("Submitting your application...", "info");

    if (formData.hasTax) {
      const totalPct =
        Number(formData.taxMarketingPct || 0) +
        Number(formData.taxDevPct || 0) +
        Number(formData.taxLpPct || 0);
      if (!formData.taxRate || Number(formData.taxRate) <= 0) {
        showStatus("Enter the total tax rate for your token.", "error");
        setIsSubmitting(false);
        return;
      }
      if (Math.abs(totalPct - 100) > 0.01) {
        showStatus("Tax recipient shares must total 100%.", "error");
        setIsSubmitting(false);
        return;
      }
    }

    if (formData.swapbackEnabled) {
      if (!formData.swapbackThreshold || Number(formData.swapbackThreshold) <= 0) {
        showStatus("Enter a swapback threshold amount.", "error");
        setIsSubmitting(false);
        return;
      }
      if (!formData.swapbackRouter || !formData.swapbackTokenOut) {
        showStatus("Enter swapback router and output token addresses.", "error");
        setIsSubmitting(false);
        return;
      }
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const apiKey = process.env.REACT_APP_API_KEY;

    if (!backendUrl || !apiKey) {
      showStatus("Bridge applications are temporarily unavailable. Please try again shortly.", "error");
      setIsSubmitting(false);
      return;
    }

    const payload = {
      chain: formData.chain,
      tokenAddress: formData.tokenAddress,
      tokenName: formData.tokenName,
      symbol: formData.symbol,
      decimals: formData.decimals === "" ? null : Number(formData.decimals),
      ownerAddress: formData.ownerAddress,
      contactEmail: formData.contactEmail,
      telegram: formData.telegram,
      website: formData.website,
      totalSupply: formData.totalSupply,
      monthlyFeeAck: formData.affordMonthly === "yes",
      additionalDetails: formData.additionalDetails,
      testTxHash: formData.testTxHash,
      hasTax: Boolean(formData.hasTax),
      taxBps: formData.hasTax && formData.taxRate ? Math.round(Number(formData.taxRate) * 100) : null,
      taxRecipients: formData.hasTax
        ? [
            {
              label: "marketing",
              wallet: formData.taxMarketingWallet,
              bps: formData.taxMarketingPct ? Math.round(Number(formData.taxMarketingPct) * 100) : 0,
            },
            {
              label: "dev",
              wallet: formData.taxDevWallet,
              bps: formData.taxDevPct ? Math.round(Number(formData.taxDevPct) * 100) : 0,
            },
            {
              label: "lp",
              wallet: formData.taxLpWallet,
              bps: formData.taxLpPct ? Math.round(Number(formData.taxLpPct) * 100) : 0,
            },
          ].filter((entry) => entry.wallet && entry.bps > 0)
        : null,
      billingWallet: formData.billingWallet,
      managementWallet: formData.managementWallet,
      swapbackEnabled: Boolean(formData.swapbackEnabled),
      swapbackThreshold: formData.swapbackEnabled ? formData.swapbackThreshold : null,
      swapbackRouter: formData.swapbackEnabled ? formData.swapbackRouter : null,
      swapbackTokenOut: formData.swapbackEnabled ? formData.swapbackTokenOut : null,
    };

    try {
      const response = await fetch(`${backendUrl}/listing/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Submission failed");
      }
      const result = await response.json();
      if (!result?.listing) {
        throw new Error("Submission failed");
      }
      setFormData(INITIAL_FORM);
      showStatus("Application submitted. Our team will review it shortly.", "success");
    } catch (error) {
      console.error("Send application error:", error);
      showStatus("Unable to submit the form right now. Please try again shortly.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="apply-page">
      <div className="apply-page__header">
        <div className="apply-page__header-content">
          <p className="apply-page__eyebrow">Bridge listing</p>
          <h1>Apply for Bridge Listing</h1>
          <p>
            Submit your production token details so our team can run the required checks and prepare your wrapped
            asset on BESC HyperChain. Applications are reviewed before activation. You will need to
            submit a test transfer transaction hash and confirm the $250 monthly maintenance fee billed on the 1st of each month.
          </p>
        </div>
        <Link to="/" className="secondary-button ghost apply-page__back">
          Back to bridge
        </Link>
      </div>
      <div className="apply-page__content">
        <div className="apply-page__panel">
          <h2>What to expect</h2>
          <ul>
            <li>Send a test transfer to our check wallet so we can detect transfer taxes and limits.</li>
            <li>Make sure the decimals match your home-chain token exactly; mismatches will block approval.</li>
            <li>After approval, we deploy your wrapped asset on BESC HyperChain and activate the bridge route.</li>
            <li>The $250 monthly maintenance fee is billed on the first business day to keep the bridge live and monitored.</li>
            <li>Liquidity provisioning is handled by your team after the wrapped asset is live.</li>
            <li>If your token has transfer tax, include the rate and destination wallets so the wrapped asset matches it.</li>
          </ul>
          {checkWallet && (
            <div className="apply-page__callout">
              <p>Check wallet for test transfer</p>
              <span>{checkWallet}</span>
            </div>
          )}
        </div>
        <form className="apply-page__form" onSubmit={handleSubmit}>
          {statusMessage && (
            <div className={`status-banner status-banner--${statusTone}`}>
              <span>{statusMessage}</span>
            </div>
          )}
          <div className="form-grid">
            <label>
              Origin chain
              <select name="chain" value={formData.chain} onChange={handleChange} required>
                <option value="bsc">BNB Smart Chain</option>
                <option value="eth">Ethereum</option>
              </select>
            </label>
            <label>
              Token contract address
              <input
                type="text"
                name="tokenAddress"
                value={formData.tokenAddress}
                onChange={handleChange}
                placeholder="0x..."
                required
              />
            </label>
            <label>
              Token / Coin Name
              <input
                type="text"
                name="tokenName"
                value={formData.tokenName}
                onChange={handleChange}
                placeholder="e.g., HyperBESC"
                required
              />
            </label>
            <label>
              Symbol
              <input
                type="text"
                name="symbol"
                value={formData.symbol}
                onChange={handleChange}
                placeholder="e.g., HBESC"
                required
              />
            </label>
            <label>
              Decimals (must match home chain)
              <input
                type="number"
                name="decimals"
                value={formData.decimals}
                onChange={handleChange}
                min="0"
                max="36"
                required
              />
            </label>
            <label>
              Owner wallet
              <input
                type="text"
                name="ownerAddress"
                value={formData.ownerAddress}
                onChange={handleChange}
                placeholder="0x..."
              />
            </label>
            <label>
              Total Supply
              <input
                type="text"
                name="totalSupply"
                value={formData.totalSupply}
                onChange={handleChange}
                placeholder="e.g., 1,000,000"
              />
            </label>
            <label>
              Telegram username
              <input
                type="text"
                name="telegram"
                value={formData.telegram}
                onChange={handleChange}
                placeholder="@yourTG"
              />
            </label>
            <label>
              Contact email
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                placeholder="ops@yourproject.com"
                required
              />
            </label>
            <label>
              Website / docs
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://yourproject.com"
              />
            </label>
            <label>
              Test transfer transaction hash
              <input
                type="text"
                name="testTxHash"
                value={formData.testTxHash}
                onChange={handleChange}
                placeholder="0x..."
                required
              />
            </label>
            <label>
              Billing wallet (monthly fee)
              <input
                type="text"
                name="billingWallet"
                value={formData.billingWallet}
                onChange={handleChange}
                placeholder="0x..."
                required
              />
            </label>
            <label>
              Management wallet (tax & swapback updates)
              <input
                type="text"
                name="managementWallet"
                value={formData.managementWallet}
                onChange={handleChange}
                placeholder="0x..."
                required
              />
            </label>
          </div>
          <div className="apply-page__question">
            <p>Does the token have a transfer tax?</p>
            <div className="apply-page__radios">
              <label>
                <input
                  type="radio"
                  name="hasTax"
                  value="true"
                  checked={formData.hasTax === true}
                  onChange={() => setFormData((prev) => ({ ...prev, hasTax: true }))}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="hasTax"
                  value="false"
                  checked={formData.hasTax === false}
                  onChange={() => setFormData((prev) => ({ ...prev, hasTax: false }))}
                />
                No
              </label>
            </div>
          </div>
          {formData.hasTax && (
            <div className="form-grid form-grid--tax">
              <label>
                Total tax rate (%)
                <input
                  type="number"
                  name="taxRate"
                  value={formData.taxRate}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </label>
              <label>
                Marketing wallet
                <input
                  type="text"
                  name="taxMarketingWallet"
                  value={formData.taxMarketingWallet}
                  onChange={handleChange}
                  placeholder="0x..."
                  required
                />
              </label>
              <label>
                Marketing share (%)
                <input
                  type="number"
                  name="taxMarketingPct"
                  value={formData.taxMarketingPct}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </label>
              <label>
                Dev wallet
                <input
                  type="text"
                  name="taxDevWallet"
                  value={formData.taxDevWallet}
                  onChange={handleChange}
                  placeholder="0x..."
                  required
                />
              </label>
              <label>
                Dev share (%)
                <input
                  type="number"
                  name="taxDevPct"
                  value={formData.taxDevPct}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </label>
              <label>
                LP wallet
                <input
                  type="text"
                  name="taxLpWallet"
                  value={formData.taxLpWallet}
                  onChange={handleChange}
                  placeholder="0x..."
                  required
                />
              </label>
              <label>
                LP share (%)
                <input
                  type="number"
                  name="taxLpPct"
                  value={formData.taxLpPct}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />
              </label>
              <div className="form-note">
                Shares must total 100%. The bridge minter wallet is automatically tax-exempt.
              </div>
            </div>
          )}
          <div className="apply-page__question">
            <p>Enable swapback for taxes?</p>
            <div className="apply-page__radios">
              <label>
                <input
                  type="radio"
                  name="swapbackEnabled"
                  value="true"
                  checked={formData.swapbackEnabled === true}
                  onChange={() => setFormData((prev) => ({ ...prev, swapbackEnabled: true }))}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="swapbackEnabled"
                  value="false"
                  checked={formData.swapbackEnabled === false}
                  onChange={() => setFormData((prev) => ({ ...prev, swapbackEnabled: false }))}
                />
                No
              </label>
            </div>
          </div>
          {formData.swapbackEnabled && (
            <div className="form-grid form-grid--tax">
              <label>
                Swapback threshold (token amount)
                <input
                  type="number"
                  name="swapbackThreshold"
                  value={formData.swapbackThreshold}
                  onChange={handleChange}
                  min="0"
                  step="0.0001"
                  required
                />
              </label>
              <label>
                Swapback router address
                <input
                  type="text"
                  name="swapbackRouter"
                  value={formData.swapbackRouter}
                  onChange={handleChange}
                  placeholder="0x..."
                  required
                />
              </label>
              <label>
                Swapback output token (WNATIVE)
                <input
                  type="text"
                  name="swapbackTokenOut"
                  value={formData.swapbackTokenOut}
                  onChange={handleChange}
                  placeholder="0x..."
                  required
                />
              </label>
              <div className="form-note">
                Swapback swaps collected taxes into the chain native asset and distributes to tax wallets.
              </div>
            </div>
          )}
          <div className="apply-page__question">
            <p>Can you pay the $250 monthly maintenance fee?</p>
            <div className="apply-page__radios">
              <label>
                <input
                  type="radio"
                  name="affordMonthly"
                  value="yes"
                  checked={formData.affordMonthly === "yes"}
                  onChange={handleChange}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="affordMonthly"
                  value="no"
                  checked={formData.affordMonthly === "no"}
                  onChange={handleChange}
                />
                No
              </label>
            </div>
          </div>
          <label className="form-label--full">
            Additional context
            <textarea
              name="additionalDetails"
              value={formData.additionalDetails}
              onChange={handleChange}
              placeholder="Tell us about audits, compliance, or liquidity partners."
              rows="4"
            />
          </label>
          <div className="apply-page__form-footer">
            <button type="submit" className="primary-button" disabled={isSubmitting}>
              Submit Application
            </button>
            <Link to="/" className="secondary-button ghost">
              Back to bridge
            </Link>
            <Link to="/billing" className="secondary-button ghost">
              Pay monthly fee
            </Link>
          </div>
          <p className="apply-page__subtle">
            Already listed? Use the billing and token management page to pay your fee and update tax settings.
          </p>
        </form>
      </div>
    </section>
  );
};

export default ApplyBridgePage;
